import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';

import { CustomSelect } from '../../../components/Shared/CustomSelect';
import { FormField } from '../../../components/FormField';
import { Checkbox } from '../../../components/Checkbox';
import { onInventoryChangeStockStatus, onInventoryUpdateStockLimit, onInventoryUpdateType } from '../../../store/Inventory/actions';

const stockStatusSelectConfig = {
    dropdownIndicator: (base) => ({ ...base, color: '#000000' }),
    control: (base) => ({
        ...base,
        border: '0',
        boxShadow: 'none',
        backgroundColor: '#7A5AF8',
        height: 40,
        '&:hover': {
            border: '0',
        },
    }),
    placeholder: (defaultStyles) => ({
        ...defaultStyles,
        color: '#ffffff',
        fontSize: 14,
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#ffffff',
    }),
};

export const InventoryItem = ({ dish, statuses }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { slug } = useParams();
    const [count, setCount] = useState(dish.stock_limit || '');

    const statusesOptions = Object.keys(statuses || {}).map((key) => ({
        label: t(statuses[key]),
        value: key,
    }));

    const statusesDefaultValue = dish.stock_status
        ? {
              label: t(statuses[dish.stock_status]),
              value: dish.stock_status,
          }
        : null;

    const availabilityOptions = [
        { label: t('Pick-up'), value: 'pickup' },
        {
            label: t('Dine-in'),
            value: 'dinein',
        },
        { label: t('Delivery'), value: 'delivery' },
    ];

    const stockLimit = dish.stock_limit ?? '';

    const onChangeInventoryStatus = ({ value }) => {
        dispatch(onInventoryChangeStockStatus(slug, dish, value));
    };
    const onChangeOrderType = (orderType) => {
        dispatch(onInventoryUpdateType(slug, dish, orderType));
    };

    const updateStockLimitFunc = (restId, item, limit) => {
        dispatch(onInventoryUpdateStockLimit(restId, item, limit));
    };

    const debouncedChangeHandler = useMemo(() => debounce(updateStockLimitFunc, 500), []);

    // Stop the invocation of the debounced function after unmounting
    useEffect(() => {
        return () => {
            debouncedChangeHandler.cancel();
        };
    }, []);

    return (
        <div className="flex bg-gray-50 items-center">
            <div className="w-1/2 pl-5 py-3 flex gap-4 items-center">
                <img className="w-[42px] h-[42px]" src={dish.photo || dish.photo_thumb || ''} alt="" />
                <span className="text-xs">{dish.title || dish.name || ""}</span>
            </div>
            <div className="flex justify-end w-full">
                <div className="w-[135px] pl-5 py-3">
                    <input
                        className="w-[95px] bg-white rounded-lg p-2"
                        value={count}
                        onChange={(e) => {
                            if (!isNaN(Number(e.target.value))) {
                                setCount(e.target.value);
                                debouncedChangeHandler(slug, dish, e.target.value);
                            }
                        }}
                    />
                </div>
                <div className="flex flex-col w-[170px] pl-5 py-1">
                    {availabilityOptions.map((option) => {
                        return (
                            <label key={option.value} htmlFor={`${dish.id}_${option.value}`} className="flex justify-between gap-1 items-center text-xs w-[90px]">
                                {option.label}
                                <Checkbox id={`${dish.id}_${option.value}`} defaultChecked={dish[option.value]} onChange={() => onChangeOrderType(option.value)} />
                            </label>
                        );
                    })}
                </div>
                <div className="w-[170px] pl-5 py-3">
                    <FormField>
                        <CustomSelect config={stockStatusSelectConfig} options={statusesOptions} defaultValue={statusesDefaultValue} className="w-full rounded-lg" onChange={onChangeInventoryStatus} isSearchable={false} />
                    </FormField>
                </div>
            </div>
        </div>
    );
};
